import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Loader2, Plus, Trash2, ExternalLink, KeyRound, Shield, Server, Github, CreditCard, Mail, BarChart3, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "hosting",         label: "Hosting",          icon: Server      },
  { value: "source-control",  label: "Source Control",   icon: Github      },
  { value: "payments",        label: "Payments",         icon: CreditCard  },
  { value: "email",           label: "Email",            icon: Mail        },
  { value: "analytics",       label: "Analytics",        icon: BarChart3   },
  { value: "other",           label: "Other",            icon: Briefcase   },
];

const categoryIcon = (cat) => {
  const c = CATEGORIES.find(c => c.value === cat);
  return c ? c.icon : Briefcase;
};

export default function InternalAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/internal-accounts`);
      setAccounts(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openNew = () => {
    setEditing({ service_name: "", category: "other", url: "", username_or_email: "", notes: "", renewal_date: "" });
    setDialogOpen(true);
  };

  const openEdit = (acc) => {
    setEditing({ ...acc, renewal_date: acc.renewal_date || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editing.service_name?.trim()) {
      toast.error("Service name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        service_name: editing.service_name.trim(),
        category: editing.category || "other",
        url: editing.url || null,
        username_or_email: editing.username_or_email || null,
        notes: editing.notes || null,
        renewal_date: editing.renewal_date || null,
      };
      if (editing.id) {
        await axios.put(`${API}/owner-console/internal-accounts/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/owner-console/internal-accounts`, payload);
      }
      toast.success(editing.id ? "Account updated" : "Account added");
      setDialogOpen(false);
      setEditing(null);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Remove "${acc.service_name}"?`)) return;
    try {
      await axios.delete(`${API}/owner-console/internal-accounts/${acc.id}`);
      toast.success("Account removed");
      fetchAccounts();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // Group by category
  const grouped = accounts.reduce((acc, a) => {
    const k = a.category || "other";
    if (!acc[k]) acc[k] = [];
    acc[k].push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Internal Accounts
          </h3>
          <p className="text-sm text-muted-foreground">
            Track external services (Render, GitHub, Stripe, etc.). URLs + usernames + notes only — NO passwords.
          </p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="add-internal-account-btn">
          <Plus className="h-4 w-4 mr-1" />
          Add account
        </Button>
      </div>

      <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
        <CardContent className="py-3 flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
          <Shield className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong>Security:</strong> never paste passwords or API keys here. Store those in your password manager. This list is for quick navigation, account IDs, plan tier reminders, and links to billing dashboards.
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No accounts tracked yet. Click "Add account" to start.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {CATEGORIES.filter(c => grouped[c.value]?.length).map((cat) => {
            const Icon = cat.icon;
            const list = grouped[cat.value];
            return (
              <div key={cat.value}>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Icon className="h-4 w-4" />
                  {cat.label}
                  <Badge variant="outline" className="ml-1">{list.length}</Badge>
                </h4>
                <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                  {list.map((a) => {
                    const ItemIcon = categoryIcon(a.category);
                    return (
                      <Card key={a.id} className="hover:shadow-md transition" data-testid={`internal-account-${a.id}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <ItemIcon className="h-4 w-4 text-muted-foreground" />
                              <CardTitle className="text-base">{a.service_name}</CardTitle>
                            </div>
                          </div>
                          {a.username_or_email && (
                            <CardDescription className="text-xs font-mono">{a.username_or_email}</CardDescription>
                          )}
                        </CardHeader>
                        <CardContent className="pb-3 space-y-2">
                          {a.renewal_date && (
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                              Renewal: {a.renewal_date}
                            </p>
                          )}
                          {a.notes && (
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-3">{a.notes}</p>
                          )}
                          <div className="flex items-center gap-1 pt-1 border-t">
                            {a.url && (
                              <Button size="sm" variant="ghost" asChild>
                                <a href={a.url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Open
                                </a>
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => handleDelete(a)} data-testid={`delete-account-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-md" data-testid="internal-account-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit account" : "Add account"}</DialogTitle>
            <DialogDescription>
              Track URLs and usernames. Never paste passwords or secrets.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="ia-service">Service name *</Label>
                <Input id="ia-service" value={editing.service_name} onChange={(e) => setEditing({ ...editing, service_name: e.target.value })} placeholder="e.g. Render, GitHub" data-testid="account-service-input" />
              </div>
              <div>
                <Label htmlFor="ia-cat">Category</Label>
                <select
                  id="ia-cat"
                  value={editing.category || "other"}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full mt-1 border rounded-md h-9 px-3 text-sm bg-background"
                  data-testid="account-category-select"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="ia-url">URL</Label>
                <Input id="ia-url" type="url" value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://..." data-testid="account-url-input" />
              </div>
              <div>
                <Label htmlFor="ia-user">Username or email</Label>
                <Input id="ia-user" value={editing.username_or_email || ""} onChange={(e) => setEditing({ ...editing, username_or_email: e.target.value })} placeholder="e.g. you@example.com" data-testid="account-username-input" />
              </div>
              <div>
                <Label htmlFor="ia-renewal">Renewal / billing date (optional)</Label>
                <Input
                  id="ia-renewal"
                  type="date"
                  value={editing.renewal_date || ""}
                  onChange={(e) => setEditing({ ...editing, renewal_date: e.target.value })}
                  data-testid="account-renewal-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We&apos;ll include this in your Monday Weekly Renewal Digest when it&apos;s within 30 days.
                </p>
              </div>
              <div>
                <Label htmlFor="ia-notes">Notes</Label>
                <Textarea id="ia-notes" rows={4} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Plan tier, account ID, billing link, contact info..." data-testid="account-notes-input" />
                <p className="text-xs text-muted-foreground mt-1">No passwords or API keys. Use your password manager for those.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-account-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
